// src/pages/ClaimsPage.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// ---- slug helpers (must match Dashboard/Favorites) ----
const slugify = (s = '') =>
  String(s).trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

const toSlug = (item) => {
  const name  = (item?.name  || '').trim();
  const brand = (item?.brand || '').trim();
  const image = (item?.image || '').trim();
  const key =
    (brand && name && `${brand}-${name}`) ||
    name ||
    brand ||
    image ||
    JSON.stringify(item || {});
  return slugify(key);
};

// read claims passed via navigate state, else from localStorage
const readPassedClaims = (state) => {
  if (state && Array.isArray(state.claims)) return state.claims;
  try {
    const v = JSON.parse(localStorage.getItem('wrap_claims') || '[]');
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
};

export default function ClaimsPage() {
  const navigate = useNavigate();
  const { state } = useLocation();

  const [wraps, setWraps] = useState([]);
  const [loading, setLoading] = useState(true);

  // keep claims in a Set for O(1) checks and easy removal
  const [claimSet, setClaimSet] = useState(() => new Set(readPassedClaims(state)));

  // fetch data
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await fetch((process.env.PUBLIC_URL || '') + '/wraps.json', { cache: 'no-store' });
        const json = await res.json();
        if (mounted) {
          const arr = Array.isArray(json) ? json : (Array.isArray(json?.data) ? json.data : []);
          setWraps(arr);
        }
      } catch {
        if (mounted) setWraps([]);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // persist whenever claimSet changes
  useEffect(() => {
    try {
      localStorage.setItem('wrap_claims', JSON.stringify(Array.from(claimSet)));
    } catch {}
  }, [claimSet]);

  // compute claimed items (support legacy name-based slug as fallback)
  const items = useMemo(() => {
    if (claimSet.size === 0) return [];
    return wraps.filter((w, idx) => {
      const stable = toSlug(w);
      const legacy = slugify(w?.name || `wrap-${idx}`); // older dashboard key
      return claimSet.has(stable) || claimSet.has(legacy);
    });
  }, [wraps, claimSet]);

  // actions
  const unclaim = (w, idx) => {
    const stable = toSlug(w);
    const legacy = slugify(w?.name || `wrap-${idx}`);
    const next = new Set(claimSet);
    next.delete(stable);
    next.delete(legacy);
    setClaimSet(next);
  };

  const clearAll = () => setClaimSet(new Set());

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <button
          onClick={() => navigate(-1)}
          style={{ border: '1px solid #e5e7eb', background: '#fff', borderRadius: 8, padding: '6px 10px', cursor: 'pointer' }}
        >
          ← Back
        </button>
        <h2 style={{ margin: 0 }}>My Claimed Wraps</h2>
        {items.length > 0 && (
          <button
            onClick={clearAll}
            style={{ marginLeft: 'auto', border: '1px solid #e5e7eb', background: '#fff', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', color: '#dc2626' }}
            title="Remove all claimed wraps"
          >
            Clear all
          </button>
        )}
      </div>

      {loading ? (
        <div style={{ color: '#6b7280' }}>Loading…</div>
      ) : items.length === 0 ? (
        <div style={{ color: '#6b7280' }}>No claimed wraps yet.</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16 }}>
          {items.map((w, i) => (
            <div key={`${toSlug(w)}-${i}`} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
              <div style={{ height: 140, background: '#f3f4f6', display: 'grid', placeItems: 'center' }}>
                {w.image ? (
                  <img src={w.image} alt={w.name || 'wrap'} style={{ width: '100%', height: 140, objectFit: 'cover' }} />
                ) : (
                  <span style={{ color: '#9ca3af' }}>No image</span>
                )}
              </div>
              <div style={{ padding: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700 }}>{w.name || 'Untitled wrap'}</div>
                  <div style={{ fontSize: 12, color: '#6b7280' }}>{w.brand || ''}</div>
                </div>
                <button
                  onClick={() => unclaim(w, i)}
                  style={{ border: '1px solid #e5e7eb', background: '#fff', borderRadius: 8, padding: '6px 10px', cursor: 'pointer' }}
                  title="Unclaim this wrap"
                >
                  ✖ Unclaim
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
